// Resolves each Store Action to ordinary navigation, a specialized workflow, or an unavailable state.
import { StoreAction } from './storeActionDomain';

export type StoreActionRouterState = {
  readonly storeActionItemId: string | number | null;
  readonly navCustomreId: string | number | undefined;
  readonly navStgId: string | number | undefined;
};

export type StoreActionNavigationOutcome = {
  readonly kind: 'navigate';
  readonly location: {
    readonly pathname: string;
    readonly search: string;
    readonly hash: string;
    readonly state?: StoreActionRouterState;
  };
};

export type StoreActionUnavailableOutcome = {
  readonly kind: 'unavailable';
};

export type StoreActionSpecialOutcome = {
  readonly kind: 'special';
  readonly action: 'DSS' | 'EAITF' | 'EAITT' | 'PRICE_TAG';
};

export type StoreActionRouteOutcome =
  | StoreActionNavigationOutcome
  | StoreActionUnavailableOutcome
  | StoreActionSpecialOutcome;

const splitRedirectUrl = (
  redirectUrl: string
): {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
} => {
  const hashIndex = redirectUrl.indexOf('#');
  const beforeHash =
    hashIndex >= 0 ? redirectUrl.slice(0, hashIndex) : redirectUrl;
  const searchIndex = beforeHash.indexOf('?');

  return {
    pathname: searchIndex >= 0 ? beforeHash.slice(0, searchIndex) : beforeHash,
    search: searchIndex >= 0 ? beforeHash.slice(searchIndex) : '',
    hash: hashIndex >= 0 ? redirectUrl.slice(hashIndex) : '',
  };
};

const isValidOrdinaryRedirect = (redirectUrl: string): boolean =>
  redirectUrl.charAt(0) === '/';

const getTireBuyerSearch = (
  redirectUrl: string,
  actionId: string | number | null
): string | null => {
  if (actionId === null) {
    return null;
  }

  const queryStart = redirectUrl.indexOf('?');
  const fragmentStart = redirectUrl.indexOf('#');
  const query =
    queryStart < 0
      ? ''
      : redirectUrl.slice(
          queryStart + 1,
          fragmentStart >= 0 ? fragmentStart : redirectUrl.length
        );

  const parameters = new URLSearchParams(query);
  parameters.delete('saiid');
  parameters.append('saiid', String(actionId));
  const normalizedQuery = parameters.toString();

  return normalizedQuery ? `?${normalizedQuery}` : '';
};

const getOrdinaryNavigation = (
  item: StoreAction
): StoreActionNavigationOutcome | StoreActionUnavailableOutcome => {
  if (!isValidOrdinaryRedirect(item.redirectUrl)) {
    return { kind: 'unavailable' };
  }

  const destination = splitRedirectUrl(item.redirectUrl);

  return {
    kind: 'navigate',
    location: {
      ...destination,
      state: {
        storeActionItemId: item.storeActionItemId,
        navCustomreId: item.customerId,
        navStgId: item.externalIdentifier,
      },
    },
  };
};

export const getStoreActionRouteOutcome = (
  item: StoreAction
): StoreActionRouteOutcome => {
  if (item.isVirtual) {
    return { action: 'PRICE_TAG', kind: 'special' };
  }

  if (
    item.storeActionType === 'DSS' ||
    item.storeActionType === 'EAITF' ||
    item.storeActionType === 'EAITT'
  ) {
    // These types require DAP or transfer handling instead of following their redirect URL.
    return { action: item.storeActionType, kind: 'special' };
  }

  if (item.storeActionType === 'TBINSCOM') {
    const search = getTireBuyerSearch(item.redirectUrl, item.storeActionItemId);

    return search === null
      ? { kind: 'unavailable' }
      : {
          kind: 'navigate',
          location: {
            hash: '',
            pathname: '/receivingpo/purchaseorders',
            search,
          },
        };
  }

  return getOrdinaryNavigation(item);
};
